import * as React from 'react';

import { useBoolean, useLockFn } from 'ahooks';
import {
  Badge,
  Button,
  Divider,
  Form,
  Modal,
  notification,
  Select,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import { CheckOutlinedIcon, DeleteOutlinedIcon } from 'components';
import { useTagValue } from 'context';
import { deleteTag } from 'services';
import type { TagStatus } from 'types';
import { localizeTagStatus, PATHNAME } from 'utils';

const {
  LOCAL: {
    TAGS: { BASE_URL: TAGS_BASE_URL },
  },
} = PATHNAME;

const tagStatuses = ['Active', 'DeActive'] satisfies TagStatus[];

type FieldType = Pick<ReturnType<typeof useTagValue>, 'status'>;

const TagFormAction = () => {
  const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();
  const [deleteLoading, { toggle: toggleDeleteLoading }] = useBoolean();
  const { id, defaultStatus, mode } = useTagValue();
  const navigate = useNavigate();

  const handleDeleteTag = useLockFn(
    async ({ id }: Pick<Required<ReturnType<typeof useTagValue>>, 'id'>) => {
      toggleDeleteLoading();

      deleteTag({ id })
        .then(() => {
          navigate(TAGS_BASE_URL);
          notification.success({
            message: 'برچسب با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        })
        .finally(() => {
          toggleDeleteLoading();
        });
    },
  );

  const { label: defaultStatusLabel, color: defaultStatusColor } =
    localizeTagStatus(defaultStatus);

  return (
    <div>
      <div className='px-6 pt-4'>
        <Typography.Title level={3} className='mb-0 text-base font-medium'>
          انتشار برچسب
        </Typography.Title>
      </div>

      <Divider className='mb-8 mt-4' />

      <div className='px-6'>
        <Typography.Paragraph>
          <Badge
            text={`وضعیت فعلی: ${defaultStatusLabel}`}
            color={defaultStatusColor}
          />
        </Typography.Paragraph>
        <Form.Item<FieldType> label='وضعیت جدید' name='status' className='mb-2'>
          <Select
            allowClear
            placeholder='وضعیت جدید برچسب را انتخاب کنید'
            options={tagStatuses.map(status => {
              const { label } = localizeTagStatus(status);
              return {
                label,
                value: status,
              };
            })}
          />
        </Form.Item>
      </div>

      <div className='flex flex-wrap-reverse items-center gap-2 p-6 *:m-0 *:shrink *:grow *:basis-40'>
        {mode === 'Update' ? (
          <Form.Item>
            <Button
              block
              danger
              className='flex items-center'
              loading={deleteLoading}
              icon={<DeleteOutlinedIcon />}
              onClick={async () => {
                const confirmed = await deleteModalInstance.confirm({
                  title: 'آیا از حذف این برچسب اطمینان دارید؟',
                  width: 500,
                });
                if (id && confirmed) {
                  handleDeleteTag({ id });
                }
              }}
            >
              <span>حذف برچسب</span>
            </Button>
            {deleteModalContextHolder}
          </Form.Item>
        ) : null}

        <Form.Item>
          <Button
            block
            type='primary'
            htmlType='submit'
            icon={<CheckOutlinedIcon />}
          >
            {mode === 'Create' ? (
              <span>افزودن برچسب جدید</span>
            ) : (
              <span>ذخیره تغییرات</span>
            )}
          </Button>
        </Form.Item>
      </div>
    </div>
  );
};

export { TagFormAction };
